import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Building2, Plus, Trash2, KeyRound, Copy, LogOut, Users, RefreshCw, Monitor, Smartphone, Eye, EyeOff, Pencil, LogIn, Shield, X, Send } from "lucide-react";
import { api, apiError, fileUrl } from "../lib/api";
import { fmtDateStr } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Brand";
import { ThemeToggle } from "../components/ThemeToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";

const formatUsPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

function copy(text, label = "Copied") { navigator.clipboard.writeText(text); toast.success(label); }

const TABS = [
  { key: "companies", label: "Companies", icon: Building2 },
  { key: "users", label: "Users & Roles", icon: Users },
  { key: "referrals", label: "Referrals", icon: Send },
];

export default function SuperAdmin() {
  const { user, logout, impersonate } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("companies");
  const [companies, setCompanies] = useState([]);

  const load = useCallback(() => api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const doImpersonate = async (id) => {
    try { await impersonate(id); toast.success("Impersonating company admin"); navigate("/admin"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-900 sticky top-0 bg-slate-950/90 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="sm" />
            <span className="hidden sm:inline text-xs font-mono uppercase tracking-widest text-blue-400 border border-blue-500/30 rounded-full px-3 py-1">Super Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <span className="text-sm text-slate-400 hidden sm:inline">{user?.email}</span>
            <button data-testid="super-logout-button" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`super-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-blue-500 text-blue-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "companies" && <Companies companies={companies} reload={load} onImpersonate={doImpersonate} />}
        {tab === "users" && <UsersTab companies={companies} />}
        {tab === "referrals" && <Referrals />}
      </main>
    </div>
  );
}

function Referrals() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", contact_email: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/referrals").then(({ data }) => setRows(data.referrals || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try { await api.post("/referrals", form); toast.success("Referral added"); setOpen(false); setForm({ company_name: "", contact_name: "", contact_email: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/referrals/${r.id}`, { status: r.status === "converted" ? "sent" : "converted" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this referral?")) return;
    try { await api.delete(`/referrals/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";
  return (
    <div data-testid="referrals-panel">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-xl font-bold">Referrals</h2><p className="text-slate-400 text-sm mt-1">Track companies you've invited and whether they converted</p></div>
        <button data-testid="add-referral-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-5 py-2.5 font-semibold transition-colors"><Plus className="w-4 h-4" /> Add referral</button>
      </div>
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">Company</th><th className="text-left px-5 py-3">Contact</th><th className="text-left px-5 py-3">Sent</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Completed</th><th className="text-right px-5 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800" data-testid={`referral-row-${r.id}`}>
                <td className="px-5 py-3 font-medium">{r.company_name}</td>
                <td className="px-5 py-3 text-slate-300">{r.contact_name || "—"}{r.contact_email && <div className="text-xs text-slate-500">{r.contact_email}</div>}</td>
                <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{fmtDateStr((r.sent_at || "").slice(0, 10))}<div className="text-xs text-slate-600">by {r.created_by}</div></td>
                <td className="px-5 py-3"><button onClick={() => toggle(r)} data-testid={`referral-status-${r.id}`} className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-colors ${r.status === "converted" ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"}`}>{r.status === "converted" ? "Converted" : "Sent"}</button></td>
                <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{r.completed_at ? fmtDateStr(r.completed_at.slice(0, 10)) : "—"}</td>
                <td className="px-5 py-3 text-right"><button data-testid={`delete-referral-${r.id}`} onClick={() => del(r.id)} className="p-2 text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No referrals yet. Add one to start tracking.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="referral-dialog">
          <DialogHeader><DialogTitle>Add referral</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div><label className="text-sm font-medium text-slate-300">Company name</label><input data-testid="referral-company-input" value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} className={inputCls} placeholder="Globex Inc" /></div>
            <div><label className="text-sm font-medium text-slate-300">Contact name</label><input data-testid="referral-contact-input" value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className={inputCls} placeholder="David Porter" /></div>
            <div><label className="text-sm font-medium text-slate-300">Contact email</label><input data-testid="referral-email-input" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className={inputCls} placeholder="david@globex.com" /></div>
            <button data-testid="referral-save-button" onClick={create} disabled={saving} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-2.5 font-semibold transition-colors">{saving ? "Saving…" : "Add referral"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Companies({ companies, reload, onImpersonate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "", account_number: "" });
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [editC, setEditC] = useState(null);
  const [viewC, setViewC] = useState(null);
  const origin = window.location.origin;

  const create = async () => {
    if (!form.name || !form.admin_email || !form.admin_password) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try {
      await api.post("/companies", form);
      toast.success("Company created");
      setOpen(false); setForm({ name: "", admin_name: "", admin_email: "", admin_password: "", account_number: "" }); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this company and all its data?")) return; await api.delete(`/companies/${id}`); toast.success("Deleted"); reload(); };
  const regen = async (id) => { const { data } = await api.post(`/companies/${id}/regenerate-token`); toast.success("New API key issued"); reload(); setRevealed((r) => ({ ...r, [id]: true })); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Companies</h1>
          <p className="text-slate-400 text-sm mt-1">{companies.length} tenant{companies.length !== 1 ? "s" : ""} on the platform</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="super-new-company-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-5 py-2.5 font-semibold transition-colors"><Plus className="w-4 h-4" /> New company</button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle>Create company</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              {[["name", "Company name", "Acme Coffee Co.", "text"], ["account_number", "Client account number", "ACC-100234", "text"], ["admin_name", "Admin name", "Dana Manager", "text"], ["admin_email", "Admin email", "manager@acme.com", "email"], ["admin_password", "Admin password", "••••••••", "password"]].map(([k, label, ph, type]) => (
                <div key={k}>
                  <label className="text-sm font-medium text-slate-300">{label}</label>
                  <input data-testid={`new-company-${k}`} type={type} value={form[k]} placeholder={ph} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
                </div>
              ))}
            </div>
            <DialogFooter><button data-testid="new-company-submit" onClick={create} disabled={saving} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">{saving ? "Creating…" : "Create company"}</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {companies.map((c, i) => (
          <motion.div key={c.id} data-testid="super-admin-tenant-row" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: c.primary_color }}>{c.name[0]}</div>
                <div>
                  <h3 className="font-bold text-lg">{c.name}</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.employee_count} staff</span>
                    <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {c.admin_count} admin</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`view-company-${c.id}`} onClick={() => setViewC(c)} title="View" className="text-slate-400 hover:text-slate-100 transition-colors p-2"><Eye className="w-4 h-4" /></button>
                <button data-testid={`edit-company-${c.id}`} onClick={() => setEditC({ ...c })} title="Edit" className="text-slate-400 hover:text-blue-400 transition-colors p-2"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`impersonate-company-${c.id}`} onClick={() => onImpersonate(c.id)} title="Impersonate admin" className="text-slate-400 hover:text-emerald-400 transition-colors p-2"><LogIn className="w-4 h-4" /></button>
                <button data-testid={`delete-company-${c.id}`} onClick={() => remove(c.id)} title="Delete" className="text-slate-500 hover:text-rose-400 transition-colors p-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <Monitor className="w-4 h-4 text-slate-500 shrink-0" />
                <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-slate-300">{origin}/kiosk/{c.id}</code>
                <button onClick={() => copy(`${origin}/kiosk/${c.id}`, "Kiosk URL copied")} className="p-1.5 text-slate-400 hover:text-blue-400 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Smartphone className="w-4 h-4 text-slate-500 shrink-0" />
                <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-slate-300">{origin}/mobile/{c.id}</code>
                <button onClick={() => copy(`${origin}/mobile/${c.id}`, "Mobile URL copied")} className="p-1.5 text-slate-400 hover:text-blue-400 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <KeyRound className="w-4 h-4 text-slate-500 shrink-0" />
                <code className="flex-1 truncate bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 font-mono text-slate-300">{revealed[c.id] ? c.api_token : "•".repeat(24)}</code>
                <button onClick={() => setRevealed((r) => ({ ...r, [c.id]: !r[c.id] }))} className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors">{revealed[c.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
                <button onClick={() => copy(c.api_token, "API key copied")} className="p-1.5 text-slate-400 hover:text-blue-400 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                <button data-testid={`regen-token-${c.id}`} onClick={() => regen(c.id)} className="p-1.5 text-slate-400 hover:text-amber-400 transition-colors"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </motion.div>
        ))}
        {companies.length === 0 && <div className="col-span-full text-center py-16 text-slate-500">No companies yet. Create your first tenant.</div>}
      </div>

      <EditCompanyDialog editC={editC} setEditC={setEditC} reload={reload} />
      <Dialog open={!!viewC} onOpenChange={(o) => !o && setViewC(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="company-view-modal">
          <DialogHeader><DialogTitle>Company details</DialogTitle></DialogHeader>
          {viewC && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: viewC.primary_color }}>{viewC.name[0]}</div>
                <div><div className="text-lg font-bold">{viewC.name}</div><div className="text-xs text-slate-400 font-mono">{viewC.id}</div></div>
              </div>
              <div className="text-sm"><span className="text-slate-400">Employees: </span>{viewC.employee_count}</div>
              <div className="text-sm"><span className="text-slate-400">Admins: </span>{viewC.admin_count}</div>
              <div className="text-sm"><span className="text-slate-400">Currency: </span>{viewC.currency}</div>
              <div className="text-sm"><span className="text-slate-400">Distance unit: </span>{viewC.distance_unit || "meters"}</div>
              <div className="text-sm break-all"><span className="text-slate-400">Kiosk: </span>{origin}/kiosk/{viewC.id}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditCompanyDialog({ editC, setEditC, reload }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/company?company_id=${editC.id}`, { name: editC.name, primary_color: editC.primary_color, account_number: editC.account_number || "", currency: editC.currency, distance_unit: editC.distance_unit });
      toast.success("Company updated"); setEditC(null); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!editC} onOpenChange={(o) => !o && setEditC(null)}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="company-edit-modal">
        <DialogHeader><DialogTitle>Edit company</DialogTitle></DialogHeader>
        {editC && (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-300">Company name</label>
              <input data-testid="company-edit-name" value={editC.name} onChange={(e) => setEditC((c) => ({ ...c, name: e.target.value }))} className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Brand color</label>
              <div className="flex items-center gap-3 mt-1.5">
                <input data-testid="company-edit-color" type="color" value={editC.primary_color} onChange={(e) => setEditC((c) => ({ ...c, primary_color: e.target.value }))} className="w-12 h-11 rounded-lg bg-slate-950 border border-slate-700 cursor-pointer" />
                <input value={editC.primary_color} onChange={(e) => setEditC((c) => ({ ...c, primary_color: e.target.value }))} className="flex-1 rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 font-mono focus:border-blue-500 outline-none transition" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Client account number</label>
              <input data-testid="company-edit-account-number" value={editC.account_number || ""} onChange={(e) => setEditC((c) => ({ ...c, account_number: e.target.value }))} placeholder="ACC-100234" className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 font-mono focus:border-blue-500 outline-none transition" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-300">Currency</label>
                <input data-testid="company-edit-currency" value={editC.currency || "USD"} onChange={(e) => setEditC((c) => ({ ...c, currency: e.target.value.toUpperCase() }))} className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 font-mono focus:border-blue-500 outline-none transition" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">Distance unit</label>
                <select data-testid="company-edit-distance-unit" value={editC.distance_unit || "meters"} onChange={(e) => setEditC((c) => ({ ...c, distance_unit: e.target.value }))} className="mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition">
                  <option value="meters">Meters</option>
                  <option value="feet">Feet</option>
                  <option value="miles">Miles</option>
                  <option value="kilometers">Kilometers</option>
                </select>
              </div>
            </div>
          </div>
        )}
        <DialogFooter><button data-testid="company-edit-save" onClick={save} disabled={saving} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">{saving ? "Saving…" : "Save changes"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ROLE_META = {
  company_admin: { label: "Company Admin", cls: "bg-blue-500/10 text-blue-400" },
  processor: { label: "Processor", cls: "bg-emerald-500/10 text-emerald-400" },
};

const FUNCTIONS = [
  { key: "overview", label: "Overview" }, { key: "employees", label: "Employees" },
  { key: "timesheets", label: "Timesheets" }, { key: "schedules", label: "Schedules" },
  { key: "reports", label: "Reports" }, { key: "approvals", label: "Approvals" },
  { key: "visitors", label: "Visitors" }, { key: "messages", label: "Messages" },
  { key: "integrations", label: "Integrations" }, { key: "settings", label: "Settings" },
];
const ALL_PERMS = FUNCTIONS.map((f) => f.key);

function UsersTab({ companies }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "processor", company_id: "", company_ids: [], permissions: [], title: "", phone: "", photo_data: "", photo_path: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ email: "", name: "", password: "", role: "processor", company_id: companies[0]?.id || "", company_ids: [], permissions: ALL_PERMS, title: "", phone: "", photo_data: "", photo_path: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name || "", password: "", role: u.role, company_id: u.company_id || "", company_ids: u.company_ids || [], permissions: u.permissions?.length ? u.permissions : ALL_PERMS, title: u.title || "", phone: u.phone || "", photo_data: "", photo_path: u.photo_path || "" }); setOpen(true); };

  const toggleCompany = (cid) => setForm((f) => ({ ...f, company_ids: f.company_ids.includes(cid) ? f.company_ids.filter((x) => x !== cid) : [...f.company_ids, cid] }));
  const togglePerm = (key) => setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((x) => x !== key) : [...f.permissions, key] }));
  const onPhoto = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => setForm((f) => ({ ...f, photo_data: r.result })); r.readAsDataURL(file); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, title: form.title, phone: form.phone };
        if (form.password) payload.password = form.password;
        if (form.photo_data) payload.photo_data = form.photo_data;
        if (form.role === "company_admin") payload.company_id = form.company_id;
        else { payload.company_ids = form.company_ids; payload.permissions = form.permissions; }
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password) { toast.error("Email and password required"); return; }
        const payload = { email: form.email, name: form.name || "User", password: form.password, role: form.role, title: form.title, phone: form.phone };
        if (form.photo_data) payload.photo_data = form.photo_data;
        if (form.role === "company_admin") { if (!form.company_id) { toast.error("Select a company"); return; } payload.company_id = form.company_id; }
        else { payload.company_ids = form.company_ids; payload.permissions = form.permissions; }
        await api.post("/users", payload);
        toast.success("User created");
      }
      setSaving(false); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this user?")) return; await api.delete(`/users/${id}`); toast.success("Deleted"); load(); };

  const inputCls = "mt-1.5 w-full rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Users & Roles</h1><p className="text-slate-400 text-sm mt-1">Create payroll processors and company admins, and assign companies</p></div>
        <button data-testid="new-user-button" onClick={openNew} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 rounded-full px-5 py-2.5 font-semibold transition-colors"><Plus className="w-4 h-4" /> New user</button>
      </div>

      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3">User</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Companies</th><th className="text-right px-5 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-800" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3"><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="px-5 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${ROLE_META[u.role]?.cls || "bg-slate-800 text-slate-400"}`}>{ROLE_META[u.role]?.label || u.role}</span></td>
                <td className="px-5 py-3 text-slate-400 text-xs">{u.role === "company_admin" ? (u.company_name || "—") : (u.company_names?.length ? u.company_names.join(", ") : "none assigned")}</td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-2 text-slate-400 hover:text-blue-400 transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)} className="p-2 text-slate-400 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No processors or extra admins yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-300">Email</label>
              <input data-testid="new-user-email" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls + (editing ? " opacity-60" : "")} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Name</label>
              <input data-testid="new-user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Title</label>
              <input data-testid="new-user-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Payroll Processor" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Contact phone</label>
              <input data-testid="new-user-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatUsPhone(e.target.value) }))} placeholder="(555) 123-4567" className={inputCls + " font-mono"} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Profile picture</label>
              <div className="mt-1.5 flex items-center gap-3">
                {(form.photo_data || form.photo_path) && <img src={form.photo_data || fileUrl(form.photo_path)} alt="preview" className="w-14 h-14 rounded-full object-cover border border-slate-700" />}
                <input data-testid="new-user-photo" type="file" accept="image/*" onChange={onPhoto} className="text-sm text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-slate-800 file:text-slate-200 file:px-4 file:py-2 file:text-sm hover:file:bg-slate-700" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Password {editing && <span className="text-slate-500">(leave blank to keep)</span>}</label>
              <input data-testid="new-user-password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Role</label>
              <select data-testid="new-user-role" value={form.role} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={inputCls + (editing ? " opacity-60" : "")}>
                <option value="processor">Payroll Processor</option>
                <option value="company_admin">Company Admin</option>
              </select>
            </div>
            {form.role === "company_admin" ? (
              <div>
                <label className="text-sm font-medium text-slate-300">Company</label>
                <select data-testid="new-user-company-select" value={form.company_id} onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))} className={inputCls}>
                  <option value="">Select company…</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium text-slate-300">Assigned companies</label>
                <div className="mt-1.5 space-y-1.5 max-h-48 overflow-y-auto border border-slate-800 rounded-xl p-3">
                  {companies.map((c) => (
                    <label key={c.id} data-testid={`user-company-checkbox-${c.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.company_ids.includes(c.id)} onChange={() => toggleCompany(c.id)} className="accent-blue-600 w-4 h-4" />
                      {c.name}
                    </label>
                  ))}
                  {companies.length === 0 && <p className="text-slate-500 text-sm">No companies to assign.</p>}
                </div>
                <label className="text-sm font-medium text-slate-300 mt-3 block">Allowed functions</label>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5 border border-slate-800 rounded-xl p-3">
                  {FUNCTIONS.map((fn) => (
                    <label key={fn.key} data-testid={`user-perm-checkbox-${fn.key}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.permissions.includes(fn.key)} onChange={() => togglePerm(fn.key)} className="accent-blue-600 w-4 h-4" />
                      {fn.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><button data-testid="save-user-button" onClick={save} disabled={saving} className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 font-semibold disabled:opacity-60 transition-colors">{editing ? "Save changes" : "Create user"}</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
